import { initializeBlazeSlider } from '../../common/blaze-slider/blaze-slider-loader.js';
import { initHeroSwipe } from '../../common/blaze-slider/hero-swipe.js';
import { initHeroEdgeGradients } from './hero-edge-gradient.js';
import { mobileOverlayArrowCenter } from './hero-control-position.js';
import { initHeroVideos } from './hero-video.js';
import { createLayoutObserver } from '../../common/layout-observer/layout-observer.js';

const mobileHero = window.matchMedia('(max-width: 47.999rem)');
const tabletHero = window.matchMedia('(min-width: 48rem) and (max-width: 63.999rem)');
const pendingHeroGeometry = new Set();
const heroMediaGeometry = new WeakMap();
const heroGeometryReady = new WeakMap();
const heroImageFallbacks = new WeakSet();
let heroGeometryFrame = 0;
let heroResizeObserver;

function prepareHeroGeometry(measurements) {
    const updates = [...measurements].map((measure) => measure());
    return () => updates.forEach((apply) => apply?.());
}

function applyHeroGeometry(measurements) {
    prepareHeroGeometry(measurements)();
}

function flushHeroGeometry() {
    heroGeometryFrame = 0;
    const measurements = [...pendingHeroGeometry];
    pendingHeroGeometry.clear();
    applyHeroGeometry(measurements);
}

function observeHeroMedia(media, controller) {
    heroResizeObserver ||= createLayoutObserver((entries) => {
        const measurements = new Set();
        entries.forEach((entry) => {
            const controller = heroMediaGeometry.get(entry.target);
            if (!controller) return;
            if (!controller.hero.isConnected) {
                controller.dispose();
            } else if (!entry.target.isConnected) {
                heroResizeObserver.unobserve(entry.target);
                heroMediaGeometry.delete(entry.target);
            } else {
                measurements.add(controller.measure);
            }
        });
        // Native deliveries follow layout and precede paint. A header-size
        // update can deliver again in the same frame; retain that fresh geometry
        // before painting controls or starting the viewport-deferred slider.
        return prepareHeroGeometry(measurements);
    });
    heroMediaGeometry.set(media, controller);
    heroResizeObserver.observe(media);
}

function setHeroImageFallback(media, failed) {
    media?.toggleAttribute('data-hero-image-error', failed);
    media
        ?.querySelector('[data-hero-desktop-alt]')
        ?.toggleAttribute('data-hero-image-error', failed);
}

export function syncHeroImages(root = document) {
    root.querySelectorAll?.('.z-home-hero__media').forEach((media) => {
        const image = media.querySelector('[data-hero-desktop-alt]');
        if (!image) {
            setHeroImageFallback(media, true);
            return;
        }
        if (!heroImageFallbacks.has(image)) {
            heroImageFallbacks.add(image);
            const updateFallback = () => {
                // Preserve the image box and slide copy over the theme backdrop.
                // A successful responsive source clears an earlier image failure.
                const failed =
                    image.complete &&
                    image.naturalWidth === 0 &&
                    (!image.hasAttribute('data-hero-deferred') ||
                        Boolean(image.currentSrc || image.getAttribute('src')));
                setHeroImageFallback(media, failed);
            };
            image.addEventListener('load', updateFallback);
            image.addEventListener('error', updateFallback);
            updateFallback();
        }
        image.alt = mobileHero.matches
            ? image.dataset.heroMobileAlt || ''
            : tabletHero.matches
              ? image.dataset.heroTabletAlt || image.dataset.heroDesktopAlt || ''
              : image.dataset.heroDesktopAlt || '';
    });
}

export function initHeroSliderCounters(root = document) {
    root.querySelectorAll?.('.z-home-hero-slider').forEach((hero) => {
        const counter = hero.querySelector('[data-home-hero-counter]');
        const track = hero.querySelector('[data-home-hero-slides]');
        if (!track || hero.dataset.heroGeometryReady === 'true' || heroGeometryReady.has(hero))
            return;
        const current = counter?.querySelector('[data-home-hero-current]');
        const totalNode = counter?.querySelector('[data-home-hero-total]');
        const announcement = counter?.querySelector('[data-home-hero-announcement]');
        const slides = [...track.querySelectorAll('.z-home-hero')];
        const total = Math.max(1, slides.length);
        const originalTabIndexes = new WeakMap();
        let activeIndex = 0;
        let disposed = false;
        let markGeometryReady;
        heroGeometryReady.set(
            hero,
            new Promise((resolve) => {
                markGeometryReady = resolve;
            }),
        );
        const mediaElements = [
            ...new Set(
                [
                    hero,
                    ...slides,
                    ...slides.flatMap((slide) => [
                        slide.querySelector('.z-home-hero__media'),
                        slide.querySelector('.z-home-hero__content'),
                    ]),
                ].filter(Boolean),
            ),
        ];
        const dispose = () => {
            if (disposed) return;
            disposed = true;
            pendingHeroGeometry.delete(measureMediaHeight);
            window.removeEventListener('resize', scheduleMediaHeight);
            mediaElements.forEach((media) => {
                heroResizeObserver?.unobserve(media);
                heroMediaGeometry.delete(media);
            });
            heroGeometryReady.delete(hero);
            markGeometryReady(false);
        };
        const measureMediaHeight = (index = activeIndex) => {
            if (disposed) return;
            if (!hero.isConnected) {
                dispose();
                return;
            }
            const rootRect = hero.getBoundingClientRect();
            const activeSlide = slides[index] || slides[0];
            const activeMedia = activeSlide?.querySelector('.z-home-hero__media');
            const mediaRect = activeMedia?.getBoundingClientRect();
            const width = window.innerWidth;
            const mobile = width < 768;
            const tablet = width >= 768 && width < 1024;
            const overlay =
                (mobile && hero.classList.contains('z-home-hero-slider--mobile-text-overlay')) ||
                (tablet && hero.classList.contains('z-home-hero-slider--tablet-text-overlay'));
            const shortLandscapeOverlay =
                overlay &&
                window
                    .getComputedStyle(hero)
                    .getPropertyValue('--z-hero-overlay-short-landscape')
                    .trim() === '1';
            const normalMobileOverlay = mobile && overlay && !shortLandscapeOverlay;
            const normalTabletOverlay = tablet && overlay && !shortLandscapeOverlay;
            const controls = hero.querySelector(':scope > [data-blaze-controls]');
            const buttons = [...(controls?.querySelectorAll('button') || [])];
            const counterHeight = counter?.offsetHeight || 0;
            const controlsRowHeight = Math.max(
                counterHeight,
                ...buttons.map((button) => button.offsetHeight),
            );
            const sourceSizes = [];
            const pixelRatio = window.devicePixelRatio;
            // All slides contribute their natural height to the shared stage.
            // Deferred cover sources still remain inert until the shared image
            // decode/transition barrier prepares the requested slide.
            slides.forEach((slide, slideIndex) => {
                const picture = slide.querySelector('.z-home-hero__media');
                const image = picture?.querySelector('img');
                const content = slide.querySelector('.z-home-hero__content');
                if (
                    !picture ||
                    !image ||
                    !content ||
                    window.getComputedStyle(image).objectFit !== 'cover'
                )
                    return;
                const source =
                    [...picture.querySelectorAll('source')].find(
                        (candidate) => window.matchMedia(candidate.media).matches,
                    ) || image;
                const pictureHeight = picture.getBoundingClientRect().height;
                const contentHeight = Math.max(
                    content.getBoundingClientRect().height,
                    content.scrollHeight,
                );
                const slideStyle = window.getComputedStyle(slide);
                const minimum = Number.parseFloat(slideStyle.minHeight) || 0;
                const grid = slideStyle.display === 'grid';
                const naturalHeight = Math.max(
                    minimum,
                    grid ? contentHeight : pictureHeight + contentHeight,
                );
                const imageHeight =
                    slideIndex === index
                        ? pictureHeight
                        : grid
                          ? naturalHeight
                          : Math.max(pictureHeight, naturalHeight - contentHeight);
                const ratio = Number(source.dataset.heroSourceRatio) || 1;
                const size = Math.ceil(
                    Math.max(image.getBoundingClientRect().width, imageHeight * ratio),
                );
                const candidates = (source.srcset || source.dataset.srcset || '')
                    .split(',')
                    .map((candidate) => candidate.trim().match(/^(.*?)\s+(\d+)w$/))
                    .filter(Boolean)
                    .map((candidate) => ({
                        url: new URL(candidate[1], document.baseURI).href,
                        width: Number(candidate[2]),
                    }));
                const selected = candidates.find((candidate) => candidate.url === image.currentSrc);
                const maximum = Math.max(0, ...candidates.map((candidate) => candidate.width));
                if (selected && selected.width >= Math.min(size * pixelRatio, maximum)) return;
                // The first picture and preload share a conservative server hint.
                // Refining it during its initial request can duplicate the LCP fetch.
                if (slideIndex === 0 && !image.complete) return;
                if (source.sizes !== `${size}px`) sourceSizes.push({ source, size });
            });
            const activeContent = activeSlide?.querySelector('.z-home-hero__content');
            const contentParts = [...(activeContent?.children || [])];
            const overlapProperty = mobile
                ? '--z-hero-mobile-next-section-overlap'
                : tablet
                  ? '--z-hero-tablet-next-section-overlap'
                  : '--z-hero-next-section-overlap';
            const requestedOverlap =
                window
                    .getComputedStyle(hero)
                    .getPropertyValue('--z-hero-overlap-enabled')
                    .trim() === '0'
                    ? 0
                    : Number.parseFloat(
                          window.getComputedStyle(hero).getPropertyValue(overlapProperty),
                      ) || 0;
            // Use one safe overlap for the entire rail. Measuring only active
            // copy would move the following section whenever the heading wraps.
            // The server-rendered padding reserve remains the upper bound even
            // when shorter slides have extra image or outer section space.
            const overlap = Math.min(
                requestedOverlap,
                ...slides.map((slide) => {
                    const content = slide.querySelector('.z-home-hero__content');
                    if (!content) return 0;
                    const paddingReserve = Math.max(
                        0,
                        Number.parseFloat(window.getComputedStyle(content).paddingBottom) - 16,
                    );
                    const contentBottom = Math.max(
                        rootRect.top,
                        ...[...content.children].map((part) => part.getBoundingClientRect().bottom),
                    );
                    return Math.min(
                        paddingReserve,
                        Math.max(0, rootRect.bottom - contentBottom - 16),
                    );
                }),
            );
            const mediaTop = mediaRect ? mediaRect.top - rootRect.top : 0;
            const mediaHeight = mediaRect?.height || 0;
            const centeredCounter = width <= 1730 && !normalMobileOverlay;
            const imageBehindFollowingSection =
                !mobile && activeSlide && window.getComputedStyle(activeSlide).display === 'grid';
            const counterTop =
                overlay && !normalTabletOverlay
                    ? mediaTop +
                      12 +
                      (normalMobileOverlay ? 0 : (controlsRowHeight - counterHeight) / 2)
                    : mediaTop +
                      mediaHeight -
                      12 -
                      counterHeight -
                      (imageBehindFollowingSection && centeredCounter ? overlap : 0);
            const headerHeight =
                Number.parseFloat(
                    window.getComputedStyle(hero).getPropertyValue('--z-sticky-header-height'),
                ) ||
                document.querySelector('.z-site-header__main')?.getBoundingClientRect().height ||
                0;
            const overlayArrowCenter = normalMobileOverlay
                ? mobileOverlayArrowCenter({
                      width: mediaRect?.width || rootRect.width,
                      height: mediaHeight,
                      headerHeight,
                      counter: counter
                          ? {
                                x:
                                    (mediaRect?.width || rootRect.width) -
                                    (buttons.at(-1)
                                        ? Number.parseFloat(
                                              window.getComputedStyle(buttons.at(-1)).right,
                                          ) || 12
                                        : 12) -
                                    counter.offsetWidth,
                                y: 12,
                                width: counter.offsetWidth,
                                height: counterHeight,
                            }
                          : null,
                      buttonHeight: Math.max(44, ...buttons.map((button) => button.offsetHeight)),
                      blocks: contentParts
                          .flatMap((part) =>
                              part.classList.contains('z-home-hero__content-surface')
                                  ? [...part.children]
                                  : [part],
                          )
                          .map((part) => {
                              const rect = part.getBoundingClientRect();
                              return {
                                  x: rect.x - (mediaRect?.x ?? rootRect.x),
                                  y: rect.y - (mediaRect?.y ?? rootRect.y),
                                  width: rect.width,
                                  height: rect.height,
                              };
                          }),
                  })
                : 0;
            const buttonPositions = buttons.map((button) => {
                const height = button.offsetHeight;
                return {
                    button,
                    top: normalMobileOverlay
                        ? overlayArrowCenter
                        : overlay && !normalTabletOverlay
                          ? 12 + controlsRowHeight / 2
                          : mobile
                            ? mediaHeight - 12 - height / 2
                            : mediaHeight / 2,
                };
            });
            return () => {
                const applyProperty = (name, value) => {
                    if (hero.style.getPropertyValue(name) !== value)
                        hero.style.setProperty(name, value);
                };
                applyProperty('--z-hero-effective-overlap', `${overlap}px`);
                if (mediaHeight) {
                    applyProperty('--z-hero-mobile-media-height', `${mediaHeight}px`);
                    applyProperty('--z-hero-active-media-height', `${mediaHeight}px`);
                }
                if (controls && mediaHeight) {
                    controls.style.top = `${mediaTop}px`;
                    controls.style.bottom = 'auto';
                    controls.style.height = `${mediaHeight}px`;
                    buttonPositions.forEach(({ button, top }) => {
                        button.style.top = `${top}px`;
                        button.style.bottom = 'auto';
                        button.style.transform = 'translateY(-50%)';
                    });
                }
                if (counter && mediaHeight) {
                    counter.style.top = `${counterTop}px`;
                    counter.style.bottom = 'auto';
                    counter.style.transform = centeredCounter ? 'translateX(-50%)' : 'none';
                    counter.style.removeProperty('left');
                    counter.style.removeProperty('right');
                    if (normalMobileOverlay) {
                        counter.style.right = 'max(12px, env(safe-area-inset-right))';
                        counter.style.left = 'auto';
                    } else if (overlay) {
                        counter.style.left = '50%';
                        counter.style.right = 'auto';
                    }
                }
                sourceSizes.forEach(({ source, size }) => {
                    source.sizes = `${size}px`;
                });
                const active = index + 1;
                if (current && current.textContent !== String(active))
                    current.textContent = String(active);
                if (totalNode && totalNode.textContent !== String(total))
                    totalNode.textContent = String(total);
                const message = `Slide ${active} von ${total}`;
                if (announcement && announcement.textContent !== message)
                    announcement.textContent = message;
                if (counter && counter.dataset.heroCounterReady !== 'true')
                    counter.dataset.heroCounterReady = 'true';
                if (hero.dataset.heroGeometryReady !== 'true')
                    hero.dataset.heroGeometryReady = 'true';
                markGeometryReady(true);
            };
        };
        const scheduleMediaHeight = () => {
            if (disposed) return;
            if (!hero.isConnected) {
                dispose();
                return;
            }
            pendingHeroGeometry.add(measureMediaHeight);
            if (!heroGeometryFrame)
                heroGeometryFrame = window.requestAnimationFrame(() => {
                    // A frame callback precedes layout. Measure in the following
                    // task so resize/slide writes can finish rendering first.
                    window.setTimeout(flushHeroGeometry, 0);
                });
        };
        // Controls follow the actual image boundary, including a taller mobile
        // image or a slide with longer text. This does not drive slider sizing.
        const controller = {
            hero,
            measure: measureMediaHeight,
            schedule: scheduleMediaHeight,
            dispose,
        };
        // A height-only media-query change can leave the Hero's own size intact.
        window.addEventListener('resize', scheduleMediaHeight, { passive: true });
        if (typeof ResizeObserver !== 'undefined') {
            mediaElements.forEach((media) => observeHeroMedia(media, controller));
        } else {
            scheduleMediaHeight();
        }
        const updateCounter = (index = 0) => {
            activeIndex = (((Number(index) || 0) % total) + total) % total;
            const activeSlide = slides[activeIndex];
            const focused = hero.ownerDocument.activeElement;
            if (slides.some((slide) => slide !== activeSlide && slide.contains(focused))) {
                // The decoded target is still offscreen during prepare. Keep focus
                // on visible navigation rather than scrolling its incoming content.
                const control = hero.querySelector(':scope > [data-blaze-controls] button');
                if (control) control.focus();
            }
            slides.forEach((slide, slideIndex) => {
                const active = slideIndex === activeIndex;
                if (active) {
                    slide.dataset.heroActive = 'true';
                    slide.removeAttribute('aria-hidden');
                } else {
                    delete slide.dataset.heroActive;
                    slide.setAttribute('aria-hidden', 'true');
                }
                slide.toggleAttribute('inert', !active);
                // Safari 15.4 lacks inert. Restore exact authored tabindex values
                // when activating a slide, including links in optional rich text.
                slide
                    .querySelectorAll(
                        'a[href], button, input, select, textarea, [tabindex], [contenteditable]',
                    )
                    .forEach((element) => {
                        if (active) {
                            if (!originalTabIndexes.has(element)) return;
                            const value = originalTabIndexes.get(element);
                            if (value === null) element.removeAttribute('tabindex');
                            else element.setAttribute('tabindex', value);
                            originalTabIndexes.delete(element);
                        } else {
                            if (!originalTabIndexes.has(element))
                                originalTabIndexes.set(element, element.getAttribute('tabindex'));
                            element.setAttribute('tabindex', '-1');
                        }
                    });
            });
        };
        track.addEventListener('zirbelino:slider-prepare', (event) => {
            const index = slides.indexOf(event.detail?.slide);
            if (index < 0) return;
            const media = slides[index].querySelector('.z-home-hero__media');
            const image = media?.querySelector('[data-hero-desktop-alt]');
            // Measure the incoming slide before fallback/accessibility writes
            // invalidate layout. Its geometry is available while still offscreen.
            const applyGeometry = measureMediaHeight(index);
            // Preparation also settles after the bounded request timeout. Use
            // the backdrop until a late successful load can reveal the image.
            setHeroImageFallback(media, !image?.complete || image.naturalWidth === 0);
            updateCounter(index);
            applyGeometry?.();
        });
        track.addEventListener('zirbelino:slider-change', (event) => {
            updateCounter(event.detail?.firstSlideIndex);
            scheduleMediaHeight();
        });
        updateCounter();
        if (!mediaElements.length) applyHeroGeometry([measureMediaHeight]);
    });
}

export function initHeroSliderControls(root = document) {
    root.querySelectorAll?.('.z-home-hero-slider[data-blaze-control-scope]').forEach((hero) => {
        if (hero.dataset.heroControlsReady === 'true') return;
        const track = hero.querySelector('[data-blaze-slider="home-hero"]');
        const controls = hero.querySelector(':scope > [data-blaze-controls]');
        if (!track || !controls) return;
        let initializing = false;
        let pendingButton;

        controls.querySelectorAll('[data-blaze-previous], [data-blaze-next]').forEach((button) => {
            button.addEventListener('click', (event) => {
                if (hero.classList.contains('is-blaze-ready')) return;
                event.preventDefault();
                event.stopImmediatePropagation();
                pendingButton = button;
                if (initializing) return;
                initializing = true;
                // Even a programmatic click before the first observer delivery must
                // wait for crop-aware source sizes before deferred images are hydrated.
                void (heroGeometryReady.get(hero) || Promise.resolve(true))
                    .then((ready) =>
                        ready && hero.isConnected ? initializeBlazeSlider(track) : undefined,
                    )
                    .then(() => {
                        const requestedButton = pendingButton;
                        pendingButton = undefined;
                        initializing = false;
                        if (hero.isConnected && hero.classList.contains('is-blaze-ready')) {
                            requestedButton?.click();
                        }
                    });
            });
        });
        hero.dataset.heroControlsReady = 'true';
    });
}

export function initHero(root = document) {
    root.querySelectorAll?.('[data-blaze-slider="home-hero"]').forEach((track) => {
        initHeroSwipe(track, { initialize: () => initializeBlazeSlider(track) });
    });
    syncHeroImages(root);
    initHeroSliderControls(root);
    initHeroSliderCounters(root);
    initHeroVideos(root);
    initHeroEdgeGradients(root);
}

mobileHero.addEventListener('change', () => syncHeroImages());
tabletHero.addEventListener('change', () => syncHeroImages());
